// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    image = "image",
    title = "title",
    description = "description",
    linkLabel = "linkLabel",
    link = "link",
    openInNewWindow = "openInNewWindow",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.linkLabel]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
    };
}

export interface SolutionCardStyles {
    container?: InjectableCss;
    image?: LazyImageProps;
    title?: LinkPresentationProps;
    description?: TypographyProps;
    link?: LinkPresentationProps;
}

export const solutionCardStyles: SolutionCardStyles = {};

const styles = solutionCardStyles;

export const SolutionCard: FC<OwnProps> = ({
    fields: { image, title, description, linkLabel, link, openInNewWindow },
}) => {
    const { url, title: linkTitle } = useGetLink(link);

    return (
        <StyledWrapper {...styles.container} data-test-selector="solutionCard">
            <LazyImage {...styles.image} src={image} altText={`${translate("Image of")}: ${title}`} />
            <Link {...styles.title} href={url} target={openInNewWindow ? "_blank" : "_self"}>
                {title}
            </Link>
            <Typography {...styles.description}>{parse(description, xParserOptions)}</Typography>
            {link && (
                <Link {...styles.link} href={url} target={openInNewWindow ? "_blank" : "_self"}>
                    {linkLabel || linkTitle}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SolutionCard,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.linkLabel,
                editorTemplate: "TextField",
                displayName: "Link Label",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.link,
                displayName: "Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                allowCategories: () => false,
            },
            {
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
